import { z } from 'zod';
import {
  RATE_MODE_OPTIONS,
  RateTierFormValue,
  rateModeDefaults,
  rateModeFields,
  refineRateMode,
} from '../_shared';

export { RATE_MODE_OPTIONS };
export type { RateTierFormValue };

export const GENDER_OPTIONS = [
  { value: 'MALE', label: 'Masculino' },
  { value: 'FEMALE', label: 'Femenino' },
  { value: 'OTHER', label: 'Otro' },
] as const;

export const COMPENSATION_PLAN_OPTIONS = [
  { value: 'A', label: 'Plan A' },
  { value: 'B', label: 'Plan B' },
] as const;

export const SAVINGS_MODE_OPTIONS = [
  { value: 'goal_value', label: 'Objetivo de ahorro' },
  { value: 'savings_capacity', label: 'Capacidad de ahorro' },
  { value: 'withdrawal_goal', label: 'Retiros periódicos' },
] as const;

// UI-only selector: in savings_capacity the premium is fixed and the simulator
// solves the coverage factor of an objective. This picks which objective shape
// to send (a single goal, periodic withdrawals, or none → plain projection).
export const CAPACITY_OBJECTIVE_OPTIONS = [
  { value: 'none', label: 'Sin objetivo (solo proyección)' },
  { value: 'goal', label: 'Objetivo único' },
  { value: 'periodic', label: 'Retiros periódicos' },
] as const;

export const PAYMENT_PERIODICITY_OPTIONS = [
  { value: 'MONTHLY', label: 'Mensual' },
  { value: 'QUARTERLY', label: 'Trimestral' },
  { value: 'SEMIANNUAL', label: 'Semestral' },
  { value: 'ANNUAL', label: 'Anual' },
] as const;

const sharedFields = {
  date_of_birth: z
    .string()
    .min(1, 'Requerido')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato YYYY-MM-DD'),
  gender: z.enum(['MALE', 'FEMALE', 'OTHER']),
  ...rateModeFields,
  compensation_plan: z.enum(['A', 'B']),
  payment_periodicity: z.enum(['MONTHLY', 'QUARTERLY', 'SEMIANNUAL', 'ANNUAL']),
  initial_contribution: z.coerce.number().nonnegative(),
  unscheduled_annual_contribution: z.coerce.number().nonnegative(),
  death_insured_capital: z.coerce.number().min(300, 'Mínimo 300 UF').max(20000, 'Máximo 20.000 UF'),
  accidental_death_insured_capital: z.coerce.number().nonnegative(),
  itp_insured_capital: z.coerce.number().nonnegative(),
  serious_illness_insured_capital: z.coerce.number().nonnegative(),
};

/**
 * Cross-field validation for savings_capacity: when an objective is chosen the
 * matching fields become required. Typed structurally (like `refineRateMode`)
 * to avoid a circular reference to the inferred form-values type.
 */
function refineCapacityObjective(
  values: {
    savings_mode: string;
    capacity_objective?: 'none' | 'goal' | 'periodic';
    goal?: number;
    goal_value_withdrawal_month?: number;
    withdrawal_amount?: number;
    withdrawal_period_months?: number;
  },
  ctx: z.RefinementCtx,
) {
  if (values.savings_mode !== 'savings_capacity') return;
  if (values.capacity_objective === 'goal') {
    if (!values.goal || values.goal <= 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Debe ser > 0', path: ['goal'] });
    }
    if (!values.goal_value_withdrawal_month || values.goal_value_withdrawal_month <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Debe ser > 0',
        path: ['goal_value_withdrawal_month'],
      });
    }
  } else if (values.capacity_objective === 'periodic') {
    if (!values.withdrawal_amount || values.withdrawal_amount <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Debe ser > 0',
        path: ['withdrawal_amount'],
      });
    }
    if (!values.withdrawal_period_months || values.withdrawal_period_months <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Debe ser > 0',
        path: ['withdrawal_period_months'],
      });
    }
  }
}

export const familySavingsInsuranceSchema = z.discriminatedUnion('savings_mode', [
  z.object({
    ...sharedFields,
    savings_mode: z.literal('goal_value'),
    goal: z.coerce.number().nonnegative('Debe ser ≥ 0'),
    goal_value_withdrawal_month: z.coerce.number().int().positive('Debe ser > 0'),
  }),
  z.object({
    ...sharedFields,
    savings_mode: z.literal('savings_capacity'),
    // UI-only; stripped before sending. Selects which objective shape the fixed
    // premium solves (single goal / periodic withdrawals / none). Lives only in
    // this variant — it is meaningless in the other modes. `.default('none')`
    // guards submit against an untouched (undefined) select value.
    capacity_objective: z.enum(['none', 'goal', 'periodic']).default('none'),
    agreed_premium: z.coerce.number().min(2, 'Mínimo 2 UF'),
    // Optional objective; required subfields enforced by refineCapacityObjective.
    // The backend solves the coverage factor of whichever objective is present
    // (goal or periodic withdrawals); the two are mutually exclusive.
    goal: z.coerce.number().nonnegative('Debe ser ≥ 0').optional(),
    goal_value_withdrawal_month: z.coerce.number().int().positive('Debe ser > 0').optional(),
    withdrawal_amount: z.coerce.number().nonnegative('Debe ser ≥ 0').optional(),
    withdrawal_period_months: z.coerce.number().int().positive('Debe ser > 0').optional(),
  }),
  z.object({
    ...sharedFields,
    savings_mode: z.literal('withdrawal_goal'),
    withdrawal_amount: z.coerce.number().nonnegative('Debe ser ≥ 0'),
    withdrawal_period_months: z.coerce.number().int().nonnegative('Debe ser ≥ 0'),
  }),
]).superRefine(refineRateMode).superRefine(refineCapacityObjective);

export type FamilySavingsInsuranceFormValues = z.infer<typeof familySavingsInsuranceSchema>;

const sharedDefaults = {
  date_of_birth: '1990-01-01' as const,
  gender: 'MALE' as const,
  // Seeds the savings_capacity-only select with a controlled initial value so
  // it renders 'none' from the start (survives mode switches via keepDefaultValue).
  capacity_objective: 'none' as const,
  ...rateModeDefaults,
  projection_rate: 0.02,
  compensation_plan: 'A' as const,
  payment_periodicity: 'MONTHLY' as const,
  initial_contribution: 0,
  unscheduled_annual_contribution: 0,
  death_insured_capital: 300,
  accidental_death_insured_capital: 0,
  itp_insured_capital: 0,
  serious_illness_insured_capital: 0,
};

export const familySavingsInsuranceDefaults: FamilySavingsInsuranceFormValues = {
  ...sharedDefaults,
  savings_mode: 'goal_value',
  goal: 0,
  goal_value_withdrawal_month: 120,
};
